/**
 * Componente de proteção de rotas que verifica se o usuário está autenticado
 * Redireciona para a página de login caso o usuário não esteja autenticado
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Guarda de autenticação para proteger rotas
 *
 * @param {Object} props - Props do componente
 * @param {React.ReactNode} props.children - Componentes protegidos
 * @param {Array<string>} [props.requiredPermissions] - Permissões necessárias para acessar a rota (opcional)
 * @returns {React.ReactElement} - Componente protegido ou redirecionamento
 */
function AuthGuard({ children, requiredPermissions = [] }) {
  const { isAuthenticated, user, loading } = useAuthContext();
  const location = useLocation();

  // Se estiver carregando, mostra um indicador de carregamento
  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        Verificando autenticação...
      </div>
    );
  }

  // Se não estiver autenticado, redireciona para login
  if (!isAuthenticated) {
    // Salva a localização atual para redirecionar de volta após o login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Verifica permissões (se especificadas)
  if (requiredPermissions.length > 0 && user?.permissions) {
    const hasRequiredPermissions = requiredPermissions.every((permission) =>
      user.permissions.includes(permission),
    );

    if (!hasRequiredPermissions) {
      return <Navigate to="/acesso-negado" replace />;
    }
  }

  // Se estiver autenticado e tiver as permissões necessárias, renderiza o conteúdo protegido
  return children;
}

export default AuthGuard;
